import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { TelekitDtoCreate, TelekitDtoUpdate, TelekitView } from '../../core/_models'
import { telekitSchema } from './validation/telekitSchema'
import { createTelekit, updateTelekit } from '../../core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'
import { showAlert, showError, OptionsHelper, ButtonType } from '_metronic/helpers'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'

export const TelekitModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: telekit } = (fetchStates['telekit'] || {}) as FetchState<TelekitView>

  const initialValues = {
    empresa: telekit?.empresa
      ? { label: telekit.empresa.nomeFantasia, value: telekit.empresa.id }
      : (undefined as IOption<number> | undefined),
    ativo: telekit
      ? { label: telekit.ativo ? 'Ativo' : 'Inativo', value: telekit.ativo }
      : (undefined as IOption<boolean> | undefined),
    identificador: telekit?.identificador || '',
    idPulsus: telekit?.idPulsus || '',
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => telekitSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (telekit) {
          const updatedTelekit: TelekitDtoUpdate = {
            identificador: Number(values.identificador),
            idPulsus: Number(values.idPulsus),
            ativo: values.ativo?.value as boolean,
          }
          await updateTelekit(telekit.id as number, updatedTelekit)
        } else {
          const telekit: TelekitDtoCreate = {
            identificador: Number(values.identificador),
            idPulsus: Number(values.idPulsus),
            ativo: values.ativo?.value as boolean,
          }
          await createTelekit(values.empresa?.value as number, telekit)
        }
        await showAlert({
          title: 'Sucesso!',
          text: telekit ? 'Telekit atualizado com sucesso!' : 'Telekit cadastrado com sucesso!',
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        name='empresa'
        entity='empresa'
        fetchOptions={OptionsHelper.getOptionsEmpresas}
        isClearable={false}
        isRequired
        isDisabled={!!telekit}
        className='mb-3'
      />

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            label='Identificador'
            name='identificador'
            placeholder='Digite o identificador'
            type='number'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            label='ID Pulsus'
            name='idPulsus'
            placeholder='Digite o ID pulsus'
            type='number'
            isRequired
          />
        </div>
      </div>
      <FormSelect
        formik={formik}
        label='Status'
        name='ativo'
        placeholder='Selecione uma status'
        options={OptionsHelper.statusCadastro}
        isClearable={false}
        isRequired
        className='mb-3'
        isDisabled={formik.isSubmitting}
      />

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>

        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
